import React, { useState, useRef } from 'react';
import { Camera, Upload, X, Image as ImageIcon } from 'lucide-react';
import './PhotoCapture.css';

interface PhotoCaptureProps {
  onPhotosChange: (photos: File[]) => void;
  maxPhotos?: number;
  existingPhotos?: string[]; // URLs of existing photos
}

interface PhotoPreview {
  file: File;
  url: string;
}

const PhotoCapture: React.FC<PhotoCaptureProps> = ({
  onPhotosChange,
  maxPhotos = 10,
  existingPhotos = []
}) => {
  const [photos, setPhotos] = useState<PhotoPreview[]>([]);
  const [showCamera, setShowCamera] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    addPhotos(files);
  };

  const addPhotos = (files: File[]) => {
    const remainingSlots = maxPhotos - photos.length - existingPhotos.length;
    const filesToAdd = files.slice(0, remainingSlots);

    const newPreviews: PhotoPreview[] = filesToAdd.map(file => ({
      file,
      url: URL.createObjectURL(file)
    }));

    const updatedPhotos = [...photos, ...newPreviews];
    setPhotos(updatedPhotos);
    onPhotosChange(updatedPhotos.map(p => p.file));
  };

  const removePhoto = (index: number) => {
    const updatedPhotos = photos.filter((_, i) => i !== index);

    // Clean up object URL to prevent memory leaks
    URL.revokeObjectURL(photos[index].url);

    setPhotos(updatedPhotos);
    onPhotosChange(updatedPhotos.map(p => p.file));
  };

  const handleCameraClick = () => {
    cameraInputRef.current?.click();
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const totalPhotos = photos.length + existingPhotos.length;
  const canAddMore = totalPhotos < maxPhotos;

  return (
    <div className="photo-capture">
      <div className="photo-capture-controls">
        {canAddMore && (
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCameraClick}
              title="Take photo with camera"
            >
              <Camera size={18} />
              Take Photo
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleUploadClick}
              title="Upload from gallery"
            >
              <Upload size={18} />
              Upload
            </button>
          </>
        )}
        <span className="photo-count">
          {totalPhotos} / {maxPhotos} photos
        </span>
      </div>

      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Photo grid */}
      {(existingPhotos.length > 0 || photos.length > 0) && (
        <div className="photo-grid">
          {/* Existing photos */}
          {existingPhotos.map((url, index) => (
            <div key={`existing-${index}`} className="photo-preview">
              <img src={url} alt={`Existing photo ${index + 1}`} />
              <div className="photo-overlay">
                <span className="photo-label">Existing</span>
              </div>
            </div>
          ))}

          {/* New photos */}
          {photos.map((photo, index) => (
            <div key={`new-${index}`} className="photo-preview">
              <img src={photo.url} alt={`Photo ${index + 1}`} />
              <button
                type="button"
                className="photo-remove"
                onClick={() => removePhoto(index)}
                aria-label="Remove photo"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {totalPhotos === 0 && (
        <div className="photo-empty-state">
          <ImageIcon size={48} />
          <p>No photos added yet</p>
          <p className="photo-hint">
            Take photos with your camera or upload from your device
          </p>
        </div>
      )}
    </div>
  );
};

export default PhotoCapture;
